import React from 'react';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { getAuth } from 'firebase/auth';

// Service centralisé pour la gestion des thèmes de couleurs
export const colorThemes = {
  default: {
    name: 'FocusPips',
    colors: {
      positive: 'rgba(16, 185, 129, 0.8)', // Vert
      negative: 'rgba(239, 68, 68, 0.8)', // Rouge
      secondary: '#7c76ff', // Violet
      accent: 'rgba(99, 102, 241, 0.8)', // Indigo
      success: 'rgba(34, 197, 94, 0.8)', // Vert foncé
      warning: 'rgba(245, 158, 11, 0.8)', // Orange
      info: 'rgba(59, 130, 246, 0.8)', // Bleu
      purple: '#7c76ff' // Violet
    }
  },
  tokyo: {
    name: 'Tropical',
    colors: {
      positive: 'rgba(254, 202, 87, 0.8)', // #feca57 - Jaune soleil
      negative: 'rgba(255, 107, 107, 0.8)', // #ff6b6b - Rouge corail
      secondary: 'rgba(72, 219, 251, 0.8)', // #48dbfb - Bleu ciel
      accent: 'rgba(72, 219, 251, 0.8)', // #48dbfb - Bleu ciel
      success: 'rgba(254, 202, 87, 0.8)', // #feca57 - Jaune soleil
      warning: 'rgba(254, 202, 87, 0.8)', // #feca57 - Jaune soleil
      info: 'rgba(72, 219, 251, 0.8)', // #48dbfb - Bleu ciel
      purple: 'rgba(72, 219, 251, 0.8)' // #48dbfb - Bleu ciel
    }
  },
  mountain: {
    name: 'Crête Montagneuse',
    colors: {
      positive: 'rgba(34, 197, 94, 0.8)', // #22c55e - Vert forêt
      negative: 'rgba(239, 68, 68, 0.8)', // #ef4444 - Rouge terre
      secondary: 'rgba(107, 114, 128, 0.8)', // #6b7280 - Gris pierre
      accent: 'rgba(34, 197, 94, 0.8)', // #22c55e - Vert forêt
      success: 'rgba(34, 197, 94, 0.8)', // #22c55e - Vert forêt
      warning: 'rgba(245, 158, 11, 0.8)', // #f59e0b - Orange coucher
      info: 'rgba(107, 114, 128, 0.8)', // #6b7280 - Gris pierre
      purple: 'rgba(34, 197, 94, 0.8)' // #22c55e - Vert forêt
    }
  },
  sunset: {
    name: 'Crépuscule Doré',
    colors: {
      positive: 'rgba(251, 146, 60, 0.8)', // #fb923c - Orange doré
      negative: 'rgba(239, 68, 68, 0.8)', // #ef4444 - Rouge coucher
      secondary: 'rgba(168, 85, 247, 0.8)', // #a855f7 - Violet crépuscule
      accent: 'rgba(251, 146, 60, 0.8)', // #fb923c - Orange doré
      success: 'rgba(251, 146, 60, 0.8)', // #fb923c - Orange doré
      warning: 'rgba(251, 146, 60, 0.8)', // #fb923c - Orange doré
      info: 'rgba(168, 85, 247, 0.8)', // #a855f7 - Violet crépuscule
      purple: 'rgba(168, 85, 247, 0.8)' // #a855f7 - Violet crépuscule
    }
  },
  ocean: {
    name: 'Océan Profond',
    colors: {
      positive: 'rgba(14, 165, 233, 0.8)', // #0ea5e9 - Bleu océan
      negative: 'rgba(239, 68, 68, 0.8)', // #ef4444 - Rouge corail
      secondary: 'rgba(99, 102, 241, 0.8)', // #6366f1 - Indigo profond
      accent: 'rgba(14, 165, 233, 0.8)', // #0ea5e9 - Bleu océan
      success: 'rgba(14, 165, 233, 0.8)', // #0ea5e9 - Bleu océan
      warning: 'rgba(99, 102, 241, 0.8)', // #6366f1 - Indigo profond
      info: 'rgba(14, 165, 233, 0.8)', // #0ea5e9 - Bleu océan
      purple: 'rgba(99, 102, 241, 0.8)' // #6366f1 - Indigo profond
    }
  },
  fruit: {
    name: 'Sorbet Fruité',
    colors: {
      positive: 'rgba(34, 197, 94, 0.8)', // #22c55e - Vert menthe
      negative: 'rgba(236, 72, 153, 0.8)', // #ec4899 - Rose framboise
      secondary: 'rgba(251, 146, 60, 0.8)', // #fb923c - Orange pêche
      accent: 'rgba(251, 146, 60, 0.8)', // #fb923c - Orange pêche
      success: 'rgba(34, 197, 94, 0.8)', // #22c55e - Vert menthe
      warning: 'rgba(251, 146, 60, 0.8)', // #fb923c - Orange pêche
      info: 'rgba(251, 146, 60, 0.8)', // #fb923c - Orange pêche
      purple: 'rgba(251, 146, 60, 0.8)' // #fb923c - Orange pêche
    }
  }
};

export type ColorThemeKey = keyof typeof colorThemes;

// ─── Custom Themes ────────────────────────────────────────────────────────────

export interface CustomColorTheme {
  id: string;
  name: string;
  /** 3 user-defined colors */
  color1: string; // Positif / Hausse
  color2: string; // Négatif / Baisse
  color3: string; // Accentuation
}

/** Convert a hex color to rgba with given opacity (0-1) */
function hexToRgba(hex: string, alpha: number): string {
  const clean = hex.replace('#', '');
  const full = clean.length === 3 ? clean.split('').map(c => c + c).join('') : clean;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

/** Build a full theme colors object from the 3 custom colors */
export function buildCustomThemeColors(t: CustomColorTheme) {
  const c1 = t.color1.startsWith('#') ? hexToRgba(t.color1, 0.8) : t.color1;
  const c2 = t.color2.startsWith('#') ? hexToRgba(t.color2, 0.8) : t.color2;
  const c3 = t.color3.startsWith('#') ? hexToRgba(t.color3, 0.8) : t.color3;
  return {
    positive: c1,
    negative: c2,
    secondary: c3,
    accent: c3,
    success: c1,
    warning: c3,
    info: c3,
    purple: c3,
  };
}

const CUSTOM_THEMES_LS_PREFIX = 'customColorThemes_';

function getCustomThemesKey(userId?: string): string {
  const uid = userId ?? getAuth().currentUser?.uid;
  return uid ? `${CUSTOM_THEMES_LS_PREFIX}${uid}` : CUSTOM_THEMES_LS_PREFIX;
}

export function getCustomThemes(userId?: string): CustomColorTheme[] {
  try {
    const raw = localStorage.getItem(getCustomThemesKey(userId));
    if (raw) {
      const themes = JSON.parse(raw) as CustomColorTheme[];
      // Register into colorThemes map on read
      themes.forEach(ct => {
        (colorThemes as any)[ct.id] = { name: ct.name, colors: buildCustomThemeColors(ct) };
      });
      return themes;
    }
  } catch {}
  return [];
}

function setCustomThemesLocal(themes: CustomColorTheme[], userId?: string) {
  localStorage.setItem(getCustomThemesKey(userId), JSON.stringify(themes));
  // Register custom themes into the colorThemes map so getCurrentThemeColors() works
  themes.forEach(ct => {
    (colorThemes as any)[ct.id] = { name: ct.name, colors: buildCustomThemeColors(ct) };
  });
  window.dispatchEvent(new CustomEvent('customThemesChanged', { detail: themes }));
}

export async function saveCustomThemesToFirebase(themes: CustomColorTheme[]): Promise<void> {
  const auth = getAuth();
  const userId = auth.currentUser?.uid;
  if (!userId) return;
  try {
    const ref = doc(db, 'userPreferences', userId);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      await updateDoc(ref, { customColorThemes: themes });
    } else {
      await setDoc(ref, { customColorThemes: themes });
    }
  } catch {}
}

export async function loadCustomThemesFromFirebase(): Promise<CustomColorTheme[] | null> {
  const auth = getAuth();
  const userId = auth.currentUser?.uid;
  if (!userId) return null;
  try {
    const snap = await getDoc(doc(db, 'userPreferences', userId));
    if (snap.exists()) {
      const data = snap.data();
      if (Array.isArray(data.customColorThemes)) return data.customColorThemes as CustomColorTheme[];
    }
  } catch {}
  return null;
}

export async function addCustomTheme(theme: CustomColorTheme): Promise<void> {
  const themes = getCustomThemes();
  themes.push(theme);
  setCustomThemesLocal(themes);
  await saveCustomThemesToFirebase(themes);
}

export async function updateCustomTheme(updated: CustomColorTheme): Promise<void> {
  const themes = getCustomThemes().map(t => t.id === updated.id ? updated : t);
  setCustomThemesLocal(themes);
  await saveCustomThemesToFirebase(themes);
}

export async function deleteCustomTheme(id: string): Promise<void> {
  const themes = getCustomThemes().filter(t => t.id !== id);
  setCustomThemesLocal(themes);
  await saveCustomThemesToFirebase(themes);
}

/** Hook for custom themes — loads from Firebase once */
export function useCustomThemes() {
  const [themes, setThemes] = React.useState<CustomColorTheme[]>(getCustomThemes());
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    loadCustomThemesFromFirebase().then(fbThemes => {
      if (fbThemes) {
        setCustomThemesLocal(fbThemes);
        setThemes(fbThemes);
      }
    }).finally(() => setLoading(false));
  }, []);

  React.useEffect(() => {
    const handler = (e: CustomEvent<CustomColorTheme[]>) => setThemes(e.detail);
    window.addEventListener('customThemesChanged', handler as EventListener);
    return () => window.removeEventListener('customThemesChanged', handler as EventListener);
  }, []);

  return { themes, loading };
}

// ─── Clé localStorage user-specific ─────────────────────────────────────────
export const getColorThemeKey = (userId?: string): string => {
  const uid = userId ?? getAuth().currentUser?.uid;
  return uid ? `colorTheme_${uid}` : 'colorTheme';
};

// Fonction pour obtenir le thème actuel depuis le localStorage
export const getCurrentColorTheme = (): ColorThemeKey | string => {
  const stored = localStorage.getItem(getColorThemeKey());
  if (stored && (stored in colorThemes || stored.startsWith('custom_'))) {
    return stored;
  }
  return 'default';
};

// Fonction pour sauvegarder le thème dans Firebase
const saveColorThemeToFirebase = async (theme: ColorThemeKey): Promise<void> => {
  const auth = getAuth();
  const userId = auth.currentUser?.uid;
  
  if (!userId) return;
  
  try {
    const userPrefsRef = doc(db, 'userPreferences', userId);
    
    // Vérifier si le document existe
    const userPrefsSnap = await getDoc(userPrefsRef);
    
    if (userPrefsSnap.exists()) {
      // Mettre à jour le document existant
      await updateDoc(userPrefsRef, {
        'iaAnalysis.colorTheme': theme
      });
    } else {
      // Créer un nouveau document avec le thème
      await setDoc(userPrefsRef, {
        iaAnalysis: {
          colorTheme: theme
        }
      });
    }
  } catch (error) {
    // console.error supprimé
  }
};

// Fonction pour récupérer le thème depuis Firebase
const loadColorThemeFromFirebase = async (): Promise<string | null> => {
  const auth = getAuth();
  const userId = auth.currentUser?.uid;
  
  if (!userId) return null;
  
  try {
    const userPrefsRef = doc(db, 'userPreferences', userId);
    const userPrefsSnap = await getDoc(userPrefsRef);
    
    if (userPrefsSnap.exists()) {
      const data = userPrefsSnap.data();
      const theme = data.iaAnalysis?.colorTheme;
      if (theme && (theme in colorThemes || theme.startsWith('custom_'))) {
        return theme as string;
      }
    }
  } catch (error) {
    // console.error supprimé
  }
  
  return null;
};

// Fonction pour définir le thème actuel (preset ou custom)
export const setColorTheme = (theme: ColorThemeKey | string): void => {
  localStorage.setItem(getColorThemeKey(), theme);
  // Déclencher un événement personnalisé pour notifier les autres composants
  window.dispatchEvent(new CustomEvent('colorThemeChanged', { detail: theme }));
  
  // Sauvegarder dans Firebase de manière asynchrone
  saveColorThemeToFirebase(theme as ColorThemeKey);
};

/** Preview-only: update colors in real-time without saving to Firebase */
export const setPreviewTheme = (theme: ColorThemeKey | string): void => {
  localStorage.setItem(getColorThemeKey(), theme);
  window.dispatchEvent(new CustomEvent('colorThemeChanged', { detail: theme }));
};

// Fonction pour obtenir les couleurs du thème actuel
export const getCurrentThemeColors = () => {
  return colorThemes[getCurrentColorTheme()].colors;
};

// Hook personnalisé pour écouter les changements de thème
export const useColorTheme = () => {
  const [currentTheme, setCurrentTheme] = React.useState<ColorThemeKey | string>(getCurrentColorTheme());
  const [, forceUpdate] = React.useReducer((x: number) => x + 1, 0);
  const [isLoading, setIsLoading] = React.useState(true);

  // Charger le thème depuis Firebase au démarrage
  React.useEffect(() => {
    const loadTheme = async () => {
      try {
        const firebaseTheme = await loadColorThemeFromFirebase();
        if (firebaseTheme) {
          // Si Firebase a un thème différent du localStorage, utiliser Firebase
          const key = getColorThemeKey();
          const localTheme = localStorage.getItem(key);
          if (localTheme !== firebaseTheme) {
            localStorage.setItem(key, firebaseTheme);
            setCurrentTheme(firebaseTheme);
          }
        } else {
          // Si pas de thème dans Firebase, sauvegarder le thème local
          const localTheme = getCurrentColorTheme();
          if (localTheme !== 'default') {
            saveColorThemeToFirebase(localTheme as ColorThemeKey);
          }
        }
      } catch (error) {
        // console.error supprimé
      } finally {
        setIsLoading(false);
      }
    };

    loadTheme();
  }, []);

  React.useEffect(() => {
    const handleThemeChange = (event: CustomEvent<string>) => {
      setCurrentTheme(event.detail);
      forceUpdate(); // Force re-render even when same theme ID has updated colors
    };

    window.addEventListener('colorThemeChanged', handleThemeChange as EventListener);
    
    return () => {
      window.removeEventListener('colorThemeChanged', handleThemeChange as EventListener);
    };
  }, []);

  return {
    currentTheme,
    setCurrentTheme: setColorTheme,
    getThemeColors: () => colorThemes[currentTheme as ColorThemeKey]?.colors ?? colorThemes.default.colors,
    isLoading
  };
};
